import type { SercAgencyInput } from "generated-graphql/graphql";

import { useMutation, useQuery } from "@apollo/client";
import { Box, Grid, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { CheckboxField } from "components/Forms/CheckboxField";
import { FormTextField } from "components/Forms/FormTextField";
import { useForm } from "react-hook-form";

import {
  CREATE_SERC_AGENCY,
  GET_SERC_AGENCIES,
  GET_SERC_AGENCY,
  UPDATE_SERC_AGENCY,
} from "./schema";

type SercAgencyDialogProps = {
  onClose: () => void;
  mode: "add" | "edit";
  sercAgencyId?: string;
};

export function SercAgencyDialog({ onClose, mode, sercAgencyId }: SercAgencyDialogProps) {
  const alerts = useAlerts();

  const { data: sercData, loading: isLoadingSerc } = useQuery(GET_SERC_AGENCY, {
    fetchPolicy: "cache-and-network",
    variables: { id: sercAgencyId ?? "" },
    skip: !sercAgencyId,
  });

  const [createSercAgency, { loading: isCreating }] = useMutation(CREATE_SERC_AGENCY, {
    refetchQueries: [{ query: GET_SERC_AGENCIES }],
  });
  const [updateSercAgency, { loading: isUpdating }] = useMutation(UPDATE_SERC_AGENCY, {
    refetchQueries: [{ query: GET_SERC_AGENCIES }],
  });

  const serc = sercData?.sercAgency;

  const form = useForm<SercAgencyInput>({
    mode: "onChange",
    defaultValues: {
      state: serc?.state ?? "",
      email: serc?.email ?? "",
      recipient: serc?.recipient ?? "",
      streetAddress1: serc?.streetAddress1 ?? "",
      streetAddress2: serc?.streetAddress2 ?? "",
      city: serc?.city ?? "",
      zip: serc?.zip ?? "",
      submitsViaEmail: serc?.submitsViaEmail ?? false,
      submitsViaPortal: serc?.submitsViaPortal ?? false,
      submitsViaForm: serc?.submitsViaForm ?? false,
    },
    values: serc
      ? {
          state: serc.state,
          email: serc.email ?? "",
          recipient: serc.recipient ?? "",
          streetAddress1: serc.streetAddress1 ?? "",
          streetAddress2: serc.streetAddress2 ?? "",
          city: serc.city ?? "",
          zip: serc.zip ?? "",
          submitsViaEmail: serc.submitsViaEmail,
          submitsViaPortal: serc.submitsViaPortal,
          submitsViaForm: serc.submitsViaForm,
        }
      : undefined,
  });

  const { handleSubmit, control, getValues, formState } = form;
  const savingOrLoading = (mode === "edit" && isLoadingSerc) || isCreating || isUpdating;

  const onSubmit = async () => {
    const input = { ...getValues() };
    if (mode === "add") {
      await createSercAgency({
        variables: { input },
        onCompleted: () => {
          onClose();
          alerts.success("SERC agency created successfully");
        },
        onError: (error) => {
          alerts.error("Failed to create SERC agency", error);
        },
      });
    } else if (sercAgencyId) {
      await updateSercAgency({
        variables: { id: sercAgencyId, input },
        onCompleted: () => {
          onClose();
          alerts.success("SERC agency updated successfully");
        },
        onError: (error) => {
          alerts.error("Failed to update SERC agency", error);
        },
      });
    }
  };

  const title = mode === "add" ? "Add SERC Agency" : "Edit SERC Agency";

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title={title}
      primaryActionButtonProps={{
        loading: savingOrLoading,
        onClick: handleSubmit(onSubmit),
        disabled: !formState.isValid,
      }}
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: "100%" }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 3 }}>
            <FormTextField
              name="state"
              label="State"
              control={control}
              rules={{ required: "State is required" }}
              textFieldProps={{ required: true }}
            />
          </Grid>
          <Grid size={{ xs: 9 }}>
            <FormTextField
              name="email"
              label="Email"
              control={control}
              textFieldProps={{ type: "email" }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormTextField name="recipient" label="Recipient" control={control} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormTextField name="streetAddress1" label="Street Address 1" control={control} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormTextField name="streetAddress2" label="Street Address 2" control={control} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormTextField name="city" label="City" control={control} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormTextField name="zip" label="Zip" control={control} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Submission methods
            </Typography>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <CheckboxField name="submitsViaEmail" label="Email" control={control} />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <CheckboxField name="submitsViaPortal" label="Portal" control={control} />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <CheckboxField name="submitsViaForm" label="Form" control={control} />
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
}
